import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Form, Container, Row, Col, Card } from 'react-bootstrap';
import { useSmartApi } from './utils/apiMigrationHelper';
import { useAuth } from './context/AuthContext';

// 防抖自定義 Hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const MemberDirectory = () => {
  const [employees, setEmployees] = useState([]);
  const [storeNames, setStoreNames] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0); // 總社友數
  const [expandedCards, setExpandedCards] = useState({}); // 追蹤展開的卡片
  const debouncedSearch = useDebounce(searchQuery, 500); // 提升至 500ms 減少頻繁查詢

  // IntersectionObserver ref 用於無限滾動
  const lastMemberRef = useRef(null);

  // 切換卡片展開/收合
  const toggleCardExpand = (employeeId) => {
    setExpandedCards(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  // 🔧 修復：使用 AuthContext 獲取認證狀態
  const { token, isAuthenticated } = useAuth();
  
  // 使用智能 API
  const { getEmployees, getUserRole, hasRBACMapping } = useSmartApi();

  // 監聽視窗大小變化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 獲取所有 store_name 用於下拉選單
  const fetchStoreNames = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const response = await axios.get('/stores', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setStoreNames(response.data);
    } catch (error) {
      console.error('載入社別列表失敗');
    }
  }, [token]);

  const fetchEmployees = useCallback(async (currentPage, storeFilter, searchTerm) => {
    // 🔧 修復：確保認證完成後才載入數據
    if (!isAuthenticated || !token) {
      return;
    }

    setLoading(true);
    try {
      // 🚀 效能優化：使用後端分頁 + 後端搜尋，每次只載入 50 筆
      const response = await getEmployees({
        page: currentPage,
        store_name: storeFilter,
        search: searchTerm,
        limit: 50  // 每頁 50 筆，減少初次載入量
      });

      const employeesData = response.data.data || [];

      // 更新員工資料
      if (currentPage === 1) {
        setEmployees(employeesData);
      } else {
        setEmployees(prevEmployees => [...prevEmployees, ...employeesData]);
      }

      // 更新總數和是否有更多資料
      if (response.data.totalRecords !== undefined) {
        setTotalRecords(response.data.totalRecords);
      }
      setHasMore(response.data.hasMore || false);
    } catch (error) {
      console.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, getEmployees]);

  // 🔧 修復：只有認證完成後才載入 store 列表
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchStoreNames();
    }
  }, [isAuthenticated, token, fetchStoreNames]);

  // 🔧 修復：當搜尋條件或社別篩選改變時，重設為第一頁並重新載入
  useEffect(() => {
    if (isAuthenticated && token) {
      setPage(1);
      setEmployees([]); // 清空舊資料，避免閃爍
      fetchEmployees(1, selectedStore, debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedStore, isAuthenticated, token]);

  // 🔧 修復：只在頁碼大於 1 時載入更多資料（分頁加載）
  useEffect(() => {
    if (page > 1 && isAuthenticated && token) {
      fetchEmployees(page, selectedStore, debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // 載入更多社友 callback
  const loadMoreMembers = useCallback(() => {
    if (!loading && hasMore) {
      setPage(prevPage => prevPage + 1);
    }
  }, [loading, hasMore]);

  // 處理 store_name 下拉選單變更
  const handleStoreChange = (e) => {
    setSelectedStore(e.target.value);
    setPage(1);
    setEmployees([]);
  };

  // 處理搜尋輸入變更
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // 打開圖片 lightbox
  const handleImageClick = (src) => {
    setLightboxImage(src);
  };

  // 🔗 自動為網址添加協議前綴
  const formatWebUrl = (url) => {
    if (!url || url.trim() === '') return null;
    const trimmedUrl = url.trim();
    // 如果已經有 http:// 或 https:// 則直接返回
    if (/^https?:\/\//i.test(trimmedUrl)) {
      return trimmedUrl;
    }
    // 否則添加 https://
    return `https://${trimmedUrl}`;
  };

  // ✅ 後端搜尋：直接使用後端過濾後的結果，無需前端再次過濾
  // 後端已經根據 search 參數進行了過濾，這裡直接使用 employees
  const filteredEmployees = employees;

  // 使用 IntersectionObserver 實現平滑的無限滾動
  useEffect(() => {
    if (!lastMemberRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreMembers();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px' // 提前 200px 開始載入，讓體驗更順暢
      }
    );

    observer.observe(lastMemberRef.current);
    return () => observer.disconnect();
  }, [filteredEmployees, hasMore, loading, loadMoreMembers]);

  return (
    <Container fluid style={{ 
      padding: '20px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* 標題區域 */}
      <Row className="mb-4">
        <Col xs={12} className="text-center">
          <h2 style={{ 
            color: '#2c3e50', 
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
          }}>
            社友名錄
          </h2>
        </Col>
      </Row>

      {/* 篩選控制區域 */}
      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Form.Group>
            <Form.Label style={{ fontWeight: 'bold', color: '#34495e' }}>選擇社別:</Form.Label>
            <Form.Select
              value={selectedStore}
              onChange={handleStoreChange}
              style={{
                borderRadius: '10px',
                border: '2px solid #bdc3c7',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              <option value="">全部社別</option>
              {storeNames.map((store) => (
                <option key={store.ss_id} value={store.ss_name}>{store.ss_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        
        <Col md={4} className="mb-3">
          <Form.Group>
            <Form.Label style={{ fontWeight: 'bold', color: '#34495e' }}>搜尋社友:</Form.Label>
            <Form.Control
              type="text"
              placeholder="搜尋姓名、職位、公司名稱、公司職稱、營業項目、Email..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                borderRadius: '10px',
                border: '2px solid #bdc3c7',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            />
          </Form.Group>
        </Col>

        <Col md={4} className="mb-3">
          <Form.Group>
            <Form.Label style={{ fontWeight: 'bold', color: '#34495e' }}>地圖檢視:</Form.Label>
            <a 
              href="/IMC-map"
              className="btn btn-info w-100 text-white"
              style={{ fontWeight: 'bold' }}
            >
              🗺️ 社友地圖
            </a>
          </Form.Group>
        </Col>
      </Row>

      {/* 統計資訊 */}
      <Row className="mb-3">
        <Col xs={12} className="text-center">
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.9)',
            borderRadius: '15px',
            padding: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}>
            <span style={{ fontSize: '16px', color: '#2c3e50', fontWeight: 'bold' }}>
              {debouncedSearch || selectedStore
                ? `搜尋到 ${totalRecords} 位社友（已載入 ${filteredEmployees.length} 位）`
                : `共 ${totalRecords} 位社友（已載入 ${filteredEmployees.length} 位）`}
              {selectedStore && ` | 篩選: ${selectedStore}`}
              {debouncedSearch && ` | 搜尋: "${debouncedSearch}"`}
            </span>
          </div>
        </Col>
      </Row>

      {/* 員工名錄顯示區域 */}
      {/* 名片式顯示 - 折疊卡片設計 */}
      <Row>
          {filteredEmployees.length > 0 ? (
            filteredEmployees.map((employee, index) => {
              const isLastElement = index === filteredEmployees.length - 1;
              const isExpanded = expandedCards[employee.e_id];
              return (
              <Col
                key={`${employee.e_id}-${index}`}
                xs={12} sm={6} lg={4} xl={3}
                className="mb-3"
                ref={isLastElement ? lastMemberRef : null}
              >
                <Card className="shadow-sm border-0" style={{
                  borderRadius: '12px',
                  transition: 'all 0.2s ease-in-out'
                }}>
                  {/* 上半部：基本資訊（始終顯示） */}
                  <Card.Body className="p-3 pb-2">
                    <div className="d-flex align-items-start">
                      {/* 左側：照片 */}
                      <div className="flex-shrink-0 me-3">
                        {employee.e_photo ? (
                          <img
                            src={`/uploads/member/${employee.e_photo}`}
                            alt={employee.e_name}
                            loading="lazy"
                            style={{
                              width: '60px',
                              height: '75px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleImageClick(`/uploads/member/${employee.e_photo}`)}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{
                          display: employee.e_photo ? 'none' : 'flex',
                          width: '60px',
                          height: '75px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '8px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6c757d',
                          fontSize: '20px'
                        }}>
                          👤
                        </div>
                      </div>

                      {/* 右側：基本資訊 */}
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <h6 className="mb-1" style={{ color: '#2c3e50', fontWeight: 'bold', fontSize: '15px' }}>
                          {employee.e_name}
                        </h6>
                        <div style={{ fontSize: '12px', lineHeight: '1.3' }}>
                          <span style={{ color: '#7f8c8d' }}>{employee.store_name}</span>
                          <span style={{ color: '#3498db', fontWeight: 'bold', marginLeft: '6px' }}>
                            {employee.position_title}
                          </span>
                        </div>
                        {employee.e_comp_name && (
                          <div style={{ fontSize: '12px', color: '#555', marginTop: '4px' }}>
                            {employee.e_comp_name}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 展開/收合按鈕 */}
                    <div
                      className="text-center mt-2 pt-2 border-top"
                      style={{ cursor: 'pointer', color: '#3498db', fontSize: '13px' }}
                      onClick={() => toggleCardExpand(employee.e_id)}
                    >
                      {isExpanded ? '▲ 收合詳細資訊' : '▼ 展開詳細資訊'}
                    </div>
                  </Card.Body>

                  {/* 下半部：詳細資訊（點擊展開） */}
                  {isExpanded && (
                    <Card.Body className="pt-0 px-3 pb-3" style={{
                      borderTop: '1px dashed #e9ecef',
                      backgroundColor: '#fafafa'
                    }}>
                      {/* 公司詳細資訊 */}
                      <div className="small text-muted mt-2" style={{ lineHeight: '1.4' }}>
                        {employee.e_comp_position && (
                          <div className="mb-1"><strong>公司職稱:</strong> {employee.e_comp_position}</div>
                        )}
                        {employee.sql_title4 && (
                          <div className="mb-1"><strong>營業項目:</strong> {employee.sql_title4}</div>
                        )}
                        {employee.e_join_date && (
                          <div className="mb-1"><strong>入社日期:</strong> {new Date(employee.e_join_date).toLocaleDateString('zh-TW')}</div>
                        )}
                      </div>

                      {/* EDM 照片與聯絡資訊 */}
                      <div className="d-flex mt-2">
                        {/* EDM 照片 */}
                        <div className="flex-shrink-0 me-3">
                          {employee.EDM_photo ? (
                            <img
                              src={`/uploads/EDM/${employee.EDM_photo}`}
                              alt="EDM照片"
                              loading="lazy"
                              style={{
                                width: '70px',
                                height: '90px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                              onClick={() => handleImageClick(`/uploads/EDM/${employee.EDM_photo}`)}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div style={{
                            display: employee.EDM_photo ? 'none' : 'flex',
                            width: '70px',
                            height: '90px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '8px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6c757d',
                            fontSize: '20px'
                          }}>
                            🏢
                          </div>
                        </div>

                        {/* 聯絡資訊 */}
                        <div className="flex-grow-1 small text-muted" style={{ lineHeight: '1.4', minWidth: 0 }}>
                          {employee.e_address1 && (
                            <div className="mb-1 text-truncate" title={employee.e_address1}>
                              <strong>📍</strong> {employee.e_address1}
                            </div>
                          )}
                          {employee.e_mobile && (
                            <div className="mb-1">
                              <strong>📱</strong> <a href={`tel:${employee.e_mobile}`} className="text-decoration-none">{employee.e_mobile}</a>
                            </div>
                          )}
                          {employee.e_tel1 && (
                            <div className="mb-1">
                              <strong>☎️</strong> <a href={`tel:${employee.e_tel1}`} className="text-decoration-none">{employee.e_tel1}</a>
                            </div>
                          )}
                          {employee.e_email && (
                            <div className="mb-1 text-truncate" title={employee.e_email}>
                              <strong>📧</strong> <a href={`mailto:${employee.e_email}`} className="text-decoration-none">{employee.e_email}</a>
                            </div>
                          )}
                          {employee.e_comp_web && formatWebUrl(employee.e_comp_web) && (
                            <div className="mb-1 text-truncate" title={employee.e_comp_web}>
                              <strong>🌐</strong> <a href={formatWebUrl(employee.e_comp_web)} target="_blank" rel="noopener noreferrer" className="text-decoration-none">{employee.e_comp_web}</a>
                            </div>
                          )}
                          {employee.e_fax && (
                            <div className="mb-1"><strong>📠</strong> {employee.e_fax}</div>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  )}
                </Card>
              </Col>
              );
            })
          ) : !loading && (
            <Col xs={12} className="text-center py-5">
              <div style={{ color: '#7f8c8d', fontSize: '18px' }}>
                {debouncedSearch || selectedStore ? '沒有找到符合條件的社友' : '目前沒有社友資料'}
              </div>
            </Col>
          )}
        </Row>

      {/* 載入提示 */}
      {/* 載入指示器 */}
      {loading && (
        <Row className="mb-4">
          <Col xs={12} className="text-center">
            <div style={{ 
              marginTop: '20px', 
              color: '#7f8c8d',
              fontSize: '16px'
            }}>
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              載入中...
            </div>
          </Col>
        </Row>
      )}

      {/* 全畫面 Lightbox */}
      {lightboxImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <img
            src={lightboxImage}
            alt="照片"
            style={{
              maxWidth: '95%',
              maxHeight: '95%',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontWeight: 'bold'
            }}
            onClick={() => setLightboxImage(null)}
          >
            ×
          </button>
        </div>
      )}
    </Container>
  );
};

export default MemberDirectory;